import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import FileIcon from "@atlaskit/icon/glyph/file";
import ProgressBar, { SuccessProgressBar } from "@atlaskit/progress-bar";
import { toast } from "react-toastify";
import ImagePreview, { AvatarPreview } from "../files/image-preview";
import PdfPreview from "../files/pdf-preview";
import Comment, {
    CommentAction,
    CommentTime,
} from "@atlaskit/comment";
import MediaServicesScaleLargeIcon from "@atlaskit/icon/glyph/media-services/scale-large";
import downloadFile from "../../utils/download-file";
import { API_URL } from "../../api/api-client";
import DeleteFileModal from "./delete-file-modal";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

function FileItemForField({ isNew = false, isSingle = false, size = null, file, onSuccess, onError, onRemoved, editMode = false }) {
    const { t } = useTranslation();
    const [isDownloading, setIsDownloading] = useState(false);
    const [uploadedData, setUploadedData] = useState(null);
    const uploadingRef = useRef(false);
    const uploadedRef = useRef(false);

    useEffect(() => {
        if (file.status !== "uploading") return;
        if (uploadingRef.current) return;
        if (uploadedRef.current) return;

        uploadingRef.current = true;
        const token = window.localStorage.getItem("auth.token");
        const data = new FormData();
        data.append("file", file.file);
        window.fetch(`${API_URL}/files`, {
            method: "POST",
            body: data,
            headers: {
                "Authorization": token ? `Bearer ${JSON.parse(token)}` : undefined,
            },
        })
            .then(response => {
                if (response.ok) {
                    uploadedRef.current = true;
                    response.json().then(data => {
                        // const newData = {
                        //     newUuid: uuidv4(),
                        //     ...data,
                        // };

                        onSuccess(data);
                        setUploadedData(data);
                    });
                    // toast.success(t("attachments_success"));
                } else {
                    onError();
                    // toast.error(t("attachments_error"));
                }
            })
            .catch(error => {
                onError();
                // toast.error(t("attachments_error"));
            })
            .finally(() => {
                uploadingRef.current = false;
            });
    }, [file]);

    const download = e => {
        e.preventDefault();
        setIsDownloading(true);
        downloadFile(file)
            .then(data => {
                let element = document.createElement("a");
                element.setAttribute("href", URL.createObjectURL(data));
                element.setAttribute("download", file.name);
                element.style.display = "none";
                document.body.appendChild(element);
                element.click();
                document.body.removeChild(element);
                setTimeout(() => {
                    URL.revokeObjectURL(data);
                    element.remove();
                }, 100);
            })
            .catch(() => toast.error(t("attachments_download_error")))
            .finally(() => setIsDownloading(false));
    };

    let actions = [];

    const showButtons = file.created_at;

    if (showButtons) actions.push(<CommentAction key="download" onClick={e => download(e)} isDisabled={isDownloading}>
        {t("attachments_download")}
    </CommentAction>);
    if (showButtons && isImage(file)) actions.push(<CommentAction><ImagePreview file={file} /></CommentAction>);
    if (showButtons && isPdf(file)) actions.push(<CommentAction><PdfPreview file={file} /></CommentAction>);
    if (showButtons && file.id) actions.push(<CommentAction><DeleteFileModal file={file} onRemoved={onRemoved} onError={onRemoved} /></CommentAction>);
    if (showButtons && !file.id) actions.push(<CommentAction><button>&times;</button></CommentAction>);

    if (isNew) {
        actions = [
            <CommentAction>
                <span onClick={() => onRemoved(file)}>
                    {t("attachment_remove")}
                </span>
            </CommentAction>
        ];
    }

    let avatar = isImage(file) ? <MediaServicesScaleLargeIcon /> : <FileIcon />;

    if (isImage(file) && isSingle) {
        avatar = <AvatarPreview size={size ?? 64} file={file} />;
    }

    return <Wrapper>
        <Comment
            avatar={avatar}
            time={<CommentTime>{file.created_at && (new Date(file.created_at)).toLocaleString()}</CommentTime>}
            content={
                <>
                    <div>{file.name}</div>
                    <FileProgress>
                        {file.status === "uploading" && <ProgressBar isIndeterminate />}
                        {file.status === "uploaded" && <SuccessProgressBar value={1} />}
                    </FileProgress>
                </>
            }
            actions={actions}
        />
    </Wrapper>;
}

export default FileItemForField;

const isImage = file => {
    return /(png|jpe?g|gif)$/i.test(file.name);
};

const isPdf = file => {
    return /(pdf)$/i.test(file.name);
};

const FileProgress = styled.div`
    margin-top: 3px;
  `;

const Wrapper = styled.div`
    margin-bottom: 20px;
    font-size: 0.9rem;
  `;